import React, { createContext, useReducer, useContext } from 'react';
import { Type, UndoRedoAction } from '../hooks/useUndoRedo';
import { INote } from '../types';
import { UndoRedoContext } from './undoRedoContext';

export enum ActionTypes {
  UPDATE_NOTE = 'UPDATE_NOTE',
  SET_NOTES = 'SET_NOTES',
}

const initialState: IState = {
  notes: [],
};

export interface IState {
  notes: Array<INote>;
}

export interface IAction {
  type: string;
  payload: Array<INote>;
}

type Dispatch = {
  state: typeof initialState;
  dispatch: (action: IAction) => void;
  setNotes: (notes: INote[]) => void;
  saveAddNoteUpdates: (prevState: INote[], state: INote[]) => void;
  saveRemoveNoteUpdates: (prevState: INote[], state: INote[]) => void;
  saveNotePositionUpdates: (prevState: INote[], state: INote[]) => void;
  saveNoteContentUpdates: (prevState: INote[], state: INote[]) => void;
  saveNotesVisibilityUpdates: (prevState: INote[], state: INote[]) => void;
};

const textPresentationReducer = (state: IState, action: IAction): IState => {
  switch (action.type) {
    case ActionTypes.UPDATE_NOTE:
      return {
        ...state,
        notes: action.payload as Array<INote>,
      };
    case ActionTypes.SET_NOTES:
      return {
        ...state,
        notes: action.payload as Array<INote>,
      };
    default:
      return state;
  }
};

export const TextPresentationPageContext = createContext<Dispatch>({
  state: initialState,
  dispatch: () => false,
  setNotes: () => null,
  saveAddNoteUpdates: () => null,
  saveRemoveNoteUpdates: () => null,
  saveNotePositionUpdates: () => null,
  saveNoteContentUpdates: () => null,
  saveNotesVisibilityUpdates: () => null,
});

export const TextPresentationPageProvider: React.FC = ({ children }): JSX.Element => {
  const [state, dispatch] = useReducer(textPresentationReducer, initialState);

  const { registerUndoRedoCreator } = useContext(UndoRedoContext);

  const setNotes = (notes: INote[]) => {
    dispatch({ type: ActionTypes.SET_NOTES, payload: notes });
  };

  const saveAddNoteUpdates = registerUndoRedoCreator(Type.addNote, (payload: UndoRedoAction) => {
    setNotes(payload as INote[]);
  });

  const saveRemoveNoteUpdates = registerUndoRedoCreator(Type.removeNote, (payload: UndoRedoAction) => {
    setNotes(payload as INote[]);
  });

  const saveNotePositionUpdates = registerUndoRedoCreator(Type.moveNote, (payload: UndoRedoAction) => {
    setNotes(payload as INote[]);
  });

  const saveNoteContentUpdates = registerUndoRedoCreator(Type.updateNoteContent, (payload: UndoRedoAction) => {
    setNotes(payload as INote[]);
  });

  const saveNotesVisibilityUpdates = registerUndoRedoCreator(Type.hideNotes, (payload: UndoRedoAction) => {
    setNotes(payload as INote[]);
  });

  return (
    <TextPresentationPageContext.Provider
      value={{
        state,
        dispatch,
        setNotes,
        saveAddNoteUpdates,
        saveRemoveNoteUpdates,
        saveNotePositionUpdates,
        saveNoteContentUpdates,
        saveNotesVisibilityUpdates,
      }}
    >
      {children}
    </TextPresentationPageContext.Provider>
  );
};
